import {useTranslation} from '@aboutyou/i18n';
import React, {FC, useMemo} from 'react';
import styled, {css} from 'styled-components';
import {FilterCard} from '../../../../atoms/FilterCard/FilterCard';
import {SizeTabsWithClusters} from '../../FilterContent/Size/SizeTabsWithClusters';
import {getActiveSizeFiltersCount} from '../../FilterContent/Size/helpers';
import {SizeRunCluster, SizeTab} from '../../FilterContent/Size/types';
import {FilterFlyout} from '../FilterFlyout';
import {FilterFlyoutFooter} from '../FilterFlyoutFooter';

interface Props {
  activeFilters: Map<string, Array<string> | undefined>;
  content?: {
    clusters?: Array<SizeRunCluster>;
    tabs?: SizeTab[];
  };
  hide: VoidFunction;
  submit: VoidFunction;
  isOpen: boolean;
  onTabsReset: (slug: string) => void;
  onSizeClick: (slug: string, sizeId: string) => void;
  onTabClick: (slug: string, sizeId: string) => void;
  open: (slugs: string[]) => void;
  enableFlyoutFilter: boolean;
}

export const FilterFlyoutSizeGrouped: FC<Props> = ({
  activeFilters,
  content,
  hide,
  isOpen,
  onTabsReset,
  onSizeClick,
  onTabClick,
  open,
  submit,
  enableFlyoutFilter,
}) => {
  const {t} = useTranslation();

  const clusters = content?.clusters;
  const tabs = content?.tabs;
  const sizeRunsSlugs = useMemo(
    () => getSizeRunsSlugs({clusters, tabs}),
    [clusters, tabs],
  );

  const handleOnOpen = () => {
    open(sizeRunsSlugs);
  };

  const handleOnHide = () => {
    hide();
  };

  return (
    <div data-testid="filterFlyoutSectionSizeGrouped">
      <FilterCard
        count={getActiveSizeFiltersCount({activeFilters, clusters, tabs})}
        onClick={handleOnOpen}
        title={t('Size')}
        disableHorizontalPadding={enableFlyoutFilter}
      />
      <FilterFlyout
        data-testid="filterFlyoutSectionSizeGrouped_filterFlyout"
        onClose={handleOnHide}
        open={isOpen}
        title={t('Size')}
        primaryButtonLabel={t('Submit')}
        onPrimaryButtonClick={submit}
        secondaryButtonLabel={t('Back')}
        footer={FilterFlyoutFooter}
      >
        <StyledSizeTabsWithClusters
          $smallerBorder={enableFlyoutFilter}
          activeFilters={activeFilters}
          content={content}
          onTabsReset={onTabsReset}
          onSizeClick={onSizeClick}
          onTabClick={onTabClick}
          enableFlyoutFilter={enableFlyoutFilter}
        />
      </FilterFlyout>
    </div>
  );
};

const StyledSizeTabsWithClusters = styled(SizeTabsWithClusters)<{
  $smallerBorder?: boolean;
}>`
  & button {
    ${props =>
      props.$smallerBorder
        ? css`
            border-radius: 4px;
            border-width: 1px;
          `
        : ''}
  }
`;

const getSizeRunsSlugs = ({
  clusters,
  tabs,
}: {
  clusters?: SizeRunCluster[] | undefined;
  tabs?: SizeTab[] | undefined;
}) => {
  const filterSlugs = new Set<string>();

  if (clusters && clusters.length > 0) {
    clusters.forEach(cluster =>
      cluster?.sizeRuns.forEach(sizeRun => filterSlugs.add(sizeRun.slug)),
    );
  }

  if (tabs && tabs.length > 0) {
    tabs[0]?.clusters?.forEach(cluster =>
      cluster.sizeRuns.forEach(sizeRun => filterSlugs.add(sizeRun.slug)),
    );

    if (tabs[0]?.specialSizeFilter?.slug) {
      filterSlugs.add(tabs[0].specialSizeFilter.slug);
    }
  }

  return [...filterSlugs];
};
